import React, { useMemo, useState } from "react";

const STATUS_DETAILS = {
  NC: {
    label: "NC",
    badge: "bg-rose-100 text-rose-700 border border-rose-200",
  },
  PC: {
    label: "PC",
    badge: "bg-amber-100 text-amber-700 border border-amber-200",
  },
  CE: {
    label: "CE",
    badge: "bg-blue-100 text-blue-700 border border-blue-200",
  },
  C: {
    label: "C",
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  },
};
const STATUS_ORDER = ["NC", "PC", "CE", "C"];

const IterationTargetsCard = ({
  iteration,
  attributes = [],
  targets = [],
  onAddTarget,
  onUpdateTarget,
  onDeleteTarget,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ attributeId: "", status: "PC", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingTargetId, setEditingTargetId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ status: "PC", note: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const targetedIds = useMemo(
    () => new Set(targets.map((target) => target.attribute_id)),
    [targets],
  );

  const availableAttributes = useMemo(
    () => attributes.filter((attr) => !targetedIds.has(attr.id)),
    [attributes, targetedIds],
  );

  const attributeName = (id) =>
    attributes.find((attr) => attr.id === id)?.name || `Attribut ${id}`;

  const resetForm = () => {
    setForm({ attributeId: "", status: "PC", note: "" });
    setError(null);
    setFormOpen(false);
  };

  const handleCreate = async () => {
    if (!iteration || !onAddTarget) return;
    if (!form.attributeId) {
      setError("Choisissez un attribut à cibler.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onAddTarget(iteration.id, {
        attribute_id: Number(form.attributeId),
        target_status: form.status,
        note: form.note?.trim() || null,
      });
      resetForm();
    } catch (e) {
      setError(e.message || "Impossible d'ajouter la cible.");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (target) => {
    setEditingTargetId(target.id);
    setEditingDraft({
      status: target.target_status || "PC",
      note: target.note || "",
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingTargetId(null);
    setEditingDraft({ status: "PC", note: "" });
    setError(null);
  };

  const saveEdit = async () => {
    if (!iteration || !onUpdateTarget || !editingTargetId) return;
    setSavingEdit(true);
    setError(null);
    try {
      await onUpdateTarget(iteration.id, editingTargetId, {
        target_status: editingDraft.status,
        note: editingDraft.note?.trim() || null,
      });
      cancelEdit();
    } catch (e) {
      setError(e.message || "Impossible de mettre à jour la cible.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteTarget = async (targetId) => {
    if (!iteration || !onDeleteTarget) return;
    setDeletingId(targetId);
    setError(null);
    try {
      await onDeleteTarget(iteration.id, targetId);
    } catch (e) {
      setError(e.message || "Impossible de supprimer la cible.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!iteration) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Attributs suivis sur l'itération
          </p>
          <p className="text-xs text-slate-500">
            Ajoutez des cibles spécifiques pour aligner l'équipe sur les
            critères clés.
          </p>
        </div>
        {availableAttributes.length > 0 && (
          <button
            type="button"
            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:border-blue-300 hover:bg-white"
            onClick={() => setFormOpen((prev) => !prev)}
          >
            {formOpen ? "Fermer" : "Ajouter une cible"}
          </button>
        )}
      </div>

      {formOpen && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div className="grid gap-2 md:grid-cols-3">
            <label className="text-sm text-slate-600">
              Attribut
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={form.attributeId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, attributeId: e.target.value }))
                }
              >
                <option value="">Sélectionnez</option>
                {availableAttributes.map((attr) => (
                  <option key={attr.id} value={attr.id}>
                    {attr.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              Statut cible
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                {STATUS_ORDER.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-600 md:col-span-1">
              Note
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={form.note}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, note: e.target.value }))
                }
              />
            </label>
          </div>
          {error && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-slate-400"
              onClick={resetForm}
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={submitting}
              className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
              onClick={handleCreate}
            >
              Créer
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2.5 py-2 text-left">Attribut</th>
              <th className="px-2.5 py-2 text-left">Statut</th>
              <th className="px-2.5 py-2 text-left">Note</th>
              <th className="px-2.5 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {targets.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-2.5 py-3 text-center text-xs text-slate-500"
                >
                  Aucune cible définie pour cette itération.
                </td>
              </tr>
            )}
            {targets.map((target) => {
              const badge =
                STATUS_DETAILS[target.target_status] || STATUS_DETAILS.PC;
              const isEditing = editingTargetId === target.id;
              return (
                <tr key={target.id}>
                  <td className="px-2.5 py-2 font-medium text-slate-900">
                    {attributeName(target.attribute_id)}
                  </td>
                  <td className="px-2.5 py-2">
                    {isEditing ? (
                      <select
                        className="rounded border border-slate-200 px-2 py-1 text-xs"
                        value={editingDraft.status}
                        onChange={(e) =>
                          setEditingDraft((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      >
                        {STATUS_ORDER.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badge.badge}`}
                      >
                        {target.target_status}
                      </span>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-xs text-slate-600">
                    {isEditing ? (
                      <input
                        value={editingDraft.note}
                        onChange={(e) =>
                          setEditingDraft((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                        className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
                      />
                    ) : (
                      target.note || "—"
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-right text-xs">
                    {isEditing ? (
                      <div className="flex justify-end gap-2">
                        <button
                          className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600"
                          onClick={cancelEdit}
                        >
                          Annuler
                        </button>
                        <button
                          className="rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-60"
                          onClick={saveEdit}
                          disabled={savingEdit}
                        >
                          Enregistrer
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() => startEdit(target)}
                        >
                          Modifier
                        </button>
                        <button
                          className="text-rose-600 hover:text-rose-800 disabled:opacity-50"
                          onClick={() => deleteTarget(target.id)}
                          disabled={deletingId === target.id}
                        >
                          Supprimer
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IterationTargetsCard;
